import React, { useState, useEffect } from 'react';
import './Questionnaire.css';

const API_BASE_URL = 'https://dl6poek2ig.execute-api.us-east-1.amazonaws.com/prod';

function Questionnaire() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [submissionInfo, setSubmissionInfo] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [token, setToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendingLink, setResendingLink] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendError, setResendError] = useState('');
  const [formData, setFormData] = useState({
    howDidYouFind: '',
    incorporated: '',
    productDemo: '',
    linkedinUrl: ''
  });

  useEffect(() => {
    // Get token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    
    if (!urlToken) {
      setError('No access token provided. Please check your email for the correct link.');
      setLoading(false);
      return;
    }

    setToken(urlToken);
    validateToken(urlToken);
  }, []);

  const validateToken = async (tokenToValidate) => {
    try {
      const response = await fetch(`${API_BASE_URL}/questionnaire/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: tokenToValidate })
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        // Set email cookie for session management
        document.cookie = `aethos_user_email=${data.email}; path=/; secure; samesite=strict`;
        
        setUserEmail(data.email);
        
        // Check if questionnaire was already submitted
        if (data.already_submitted) {
          setAlreadySubmitted(true);
          setSubmissionInfo({
            message: data.message,
            status: data.status,
            submittedAt: data.submitted_at
          });
        }
        
        setLoading(false);
      } else {
        setError(data.error || 'Invalid or expired token');
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to validate access. Please try again later.');
      setLoading(false);
      console.error('Validation error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleResendLink = async (e) => {
    e.preventDefault();
    
    if (!resendEmail || !resendEmail.includes('@')) {
      setResendError('Please enter a valid email address.');
      return;
    }

    setResendingLink(true);
    setResendError('');

    try {
      const response = await fetch(`${API_BASE_URL}/questionnaire-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resendEmail })
      });

      const data = await response.json();

      if (response.ok) {
        setResendSuccess(true);
      } else {
        setResendError(data.error || 'Failed to send new link. Please try again.');
      }
    } catch (err) {
      setResendError('Failed to send new link. Please try again or contact support.');
      console.error('Resend error:', err);
    } finally {
      setResendingLink(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = ['howDidYouFind', 'incorporated', 'productDemo', 'linkedinUrl'];
    for (const field of requiredFields) {
      if (!formData[field]) {
        alert(`Please fill in all required fields.`);
        return;
      }
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/questionnaire/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          questionnaire: formData
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        alert(`Error: ${data.error || 'Failed to submit questionnaire'}`);
      }
    } catch (err) {
      alert('Failed to submit questionnaire. Please try again.');
      console.error('Submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
      return (
    <div className="App">
      <div className="video-background">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="background-video"
        >
          <source
            src="https://video.wixstatic.com/video/9a33be_ea5e9f4fa1b34803ba0c6f6bf3d1553e/1080p/mp4/file.mp4"
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>
      </div>
      <div className="content-overlay">
        <div className="questionnaire-container">
          <div className="questionnaire-card">
            <div className="questionnaire-header">
              <h1>🎯 Pre-Interview Questionnaire</h1>
              <p>Help us prepare for your interview</p>
            </div>
            <div className="questionnaire-content">
              <div className="loading">
                <div className="spinner"></div>
                <p>Validating your access...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  }

  if (error) {
    return (
      <div className="App">
        <div className="video-background">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="background-video"
          >
            <source
              src="https://video.wixstatic.com/video/9a33be_ea5e9f4fa1b34803ba0c6f6bf3d1553e/1080p/mp4/file.mp4"
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="content-overlay">
          <div className="questionnaire-container">
            <div className="questionnaire-card">
              <div className="questionnaire-header">
                <h1>🎯 Pre-Interview Questionnaire</h1>
                <p>Help us prepare for your interview</p>
              </div>
              <div className="questionnaire-content">
                <div className="error">
                  <h3>Access Denied</h3>
                  <p>{error}</p>
                  
                  {error.toLowerCase().includes('expired') && (
                    <div style={{ marginTop: '24px', padding: '20px', backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                      <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px', marginTop: 0 }}>
                        Your questionnaire link has expired. Enter your email below to receive a new link.
                      </p>
                      
                      {resendSuccess ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <div style={{ fontSize: '48px', marginBottom: '12px' }}>✓</div>
                          <p style={{ color: '#10b981', fontWeight: '600', fontSize: '16px', margin: 0 }}>
                            New link sent! Please check your email.
                          </p>
                        </div>
                      ) : (
                        <>
                          <input
                            type="email"
                            placeholder="Enter your email address"
                            value={resendEmail}
                            onChange={(e) => setResendEmail(e.target.value)}
                            disabled={resendingLink}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              fontSize: '16px',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              marginBottom: '12px',
                              boxSizing: 'border-box',
                              fontFamily: 'inherit'
                            }}
                          />
                          {resendError && (
                            <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px', marginBottom: '12px' }}>
                              {resendError}
                            </p>
                          )}
                          <button 
                            onClick={handleResendLink}
                            disabled={resendingLink || !resendEmail}
                            style={{
                              width: '100%',
                              padding: '12px 24px',
                              backgroundColor: '#4f46e5',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '16px',
                              fontWeight: '500',
                              cursor: resendingLink || !resendEmail ? 'not-allowed' : 'pointer',
                              opacity: resendingLink || !resendEmail ? 0.6 : 1,
                              transition: 'all 0.2s'
                            }}
                          >
                            {resendingLink ? 'Sending...' : 'Resend Questionnaire Link'}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="App">
        <div className="video-background">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="background-video"
          >
            <source
              src="https://video.wixstatic.com/video/9a33be_ea5e9f4fa1b34803ba0c6f6bf3d1553e/1080p/mp4/file.mp4"
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="content-overlay">
          <div className="questionnaire-container">
            <div className="questionnaire-card">
              <div className="questionnaire-header">
                <h1>🎯 Pre-Interview Questionnaire</h1>
                <p>Help us prepare for your interview</p>
              </div>
              <div className="questionnaire-content">
                <div className="success">
                  <h3>✅ Thank You!</h3>
                  <p>Your questionnaire has been submitted successfully.</p>
                  <p>We will be in touch soon regarding next steps.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (alreadySubmitted && submissionInfo) {
    return (
      <div className="App">
        <div className="video-background">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="background-video"
          >
            <source
              src="https://video.wixstatic.com/video/9a33be_ea5e9f4fa1b34803ba0c6f6bf3d1553e/1080p/mp4/file.mp4"
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="content-overlay">
          <div className="questionnaire-container">
            <div className="questionnaire-card">
              <div className="questionnaire-header">
                <h1>🎯 Pre-Interview Questionnaire</h1>
                <p>Application Status Update</p>
              </div>
              <div className="questionnaire-content">
                <div className="already-submitted">
                  <h3>✅ Already Submitted</h3>
                  <p>{submissionInfo.message}</p>
                  
                  <div className="status-info">
                    <div className="status-item">
                      <strong>Current Status:</strong>
                      <span className={`status-badge ${submissionInfo.status.toLowerCase().replace(/\s+/g, '-')}`}>
                        {submissionInfo.status}
                      </span>
                    </div>
                    
                    {submissionInfo.submittedAt && (
                      <div className="status-item">
                        <strong>Submitted:</strong>
                        <span>{new Date(submissionInfo.submittedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                    )}
                    
                    <div className="status-item">
                      <strong>Email:</strong>
                      <span>{userEmail}</span>
                    </div>
                  </div>
                  
                  <div className="next-steps">
                    <p><em>We will contact you directly via email regarding any next steps in the application process.</em></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="video-background">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="background-video"
        >
          <source
            src="https://video.wixstatic.com/video/9a33be_ea5e9f4fa1b34803ba0c6f6bf3d1553e/1080p/mp4/file.mp4"
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>
      </div>
      <div className="content-overlay">
        <div className="questionnaire-container">
          <div className="questionnaire-card">
            <div className="questionnaire-header">
              <h1>🎯 Pre-Interview Questionnaire</h1>
              <p>Help us prepare for your interview</p>
            </div>
            
            <div className="questionnaire-content">
              <div className="email-display">
                Submitting for: <strong>{userEmail}</strong>
              </div>

              <form onSubmit={handleSubmit} className="questionnaire-form">
            <div className="form-group">
              <label htmlFor="howDidYouFind">
                How did you find out about Æthos? <span className="required">*</span>
              </label>
              <select
                id="howDidYouFind"
                name="howDidYouFind"
                value={formData.howDidYouFind}
                onChange={handleInputChange}
                required
              >
                <option value="">Select an option...</option>
                <option value="social-media">Social Media</option>
                <option value="friend-referral">Friend/Colleague Referral</option>
                <option value="university">University/Academic Institution</option>
                <option value="online-search">Online Search</option>
                <option value="networking-event">Networking Event</option>
                <option value="alumni-network">Alumni Network</option>
                <option value="accelerator-program">Other Accelerator/Incubator</option>
                <option value="news-article">News Article/Press</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="incorporated">
                Have you incorporated? <span className="required">*</span>
              </label>
              <select
                id="incorporated"
                name="incorporated"
                value={formData.incorporated}
                onChange={handleInputChange}
                required
              >
                <option value="">Select an option...</option>
                <option value="yes">Yes, we have incorporated</option>
                <option value="no">No, not yet</option>
                <option value="in-progress">Incorporation in progress</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="productDemo">
                Send a link to the product demo <span className="required">*</span>
              </label>
              <input
                type="url"
                id="productDemo"
                name="productDemo"
                value={formData.productDemo}
                onChange={handleInputChange}
                placeholder="https://..."
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="linkedinUrl">
                LinkedIn Profile URL <span className="required">*</span>
              </label>
              <input
                type="url"
                id="linkedinUrl"
                name="linkedinUrl"
                value={formData.linkedinUrl}
                onChange={handleInputChange}
                placeholder="https://linkedin.com/in/your-profile"
                required
              />
            </div>

            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Questionnaire'}
            </button>
          </form>
        </div>
      </div>
    </div>
      </div>
    </div>
  );
}

export default Questionnaire;
